// ---------------------------------------------------------------------------
// Deciding what has to leave the device when turf stops being assigned.
//
// The delta pull only ever writes: it asks for rows changed since a cursor and
// puts them. Nothing tells it that a row it fetched last week has since been
// deleted, so a turf an organizer removes -- or reassigns to someone else --
// stays in IndexedDB and keeps appearing in the app (PLAN 5.6).
//
// Pure, and separate from sync.ts, so the rules can be tested without a
// database. The rule that matters is the second one: unsynced work is never
// discarded to tidy up.
// ---------------------------------------------------------------------------

/** Local turf the canvasser is no longer assigned. */
export function staleTurfIds(
  local: readonly { id: string }[],
  assigned: readonly string[],
): string[] {
  const keep = new Set(assigned);
  return local.filter((t) => !keep.has(t.id)).map((t) => t.id);
}

/**
 * Households to evict, given the turf going away and the households still
 * holding unsynced work.
 *
 * A household with a queued visit stays, even though its turf has gone. The
 * visit is a knock that happened and has not reached the server; dropping the
 * address would leave the sync screen naming a UUID, and dropping the visit
 * would destroy the record. Both are worse than a stale row that clears itself
 * on the next sync after the queue drains.
 */
export function evictableHouseholds(
  households: readonly { id: string; turf_id: string | null }[],
  stale: ReadonlySet<string>,
  holding: ReadonlySet<string>,
): string[] {
  return households
    .filter((h) => h.turf_id !== null && stale.has(h.turf_id) && !holding.has(h.id))
    .map((h) => h.id);
}

/**
 * Households that have left the canvasser's turf while the turf itself stayed
 * assigned, given what the server says each one's turf is now.
 *
 * `current` holds only the homes the server still lets this canvasser read,
 * so a missing id is a home moved out of their turf -- the delta pull cannot
 * say so, because it only asks about homes inside it. A home whose turf moved
 * to another one this device is not working is gone for the same reason.
 *
 * Homes this device has not finished telling the server about are never asked
 * about (`queued`): the server has not heard of one added at a door, and would
 * report it missing. Homes holding unsynced work stay, by the rule
 * evictableHouseholds() documents.
 */
export function householdsThatLeft(
  local: readonly string[],
  current: ReadonlyMap<string, string | null>,
  assigned: readonly string[],
  holding: ReadonlySet<string>,
): string[] {
  const turf = new Set(assigned);
  return local.filter((id) => {
    if (holding.has(id)) return false;
    const now = current.get(id);
    return now === undefined || now === null || !turf.has(now);
  });
}
